/** Reads a pasted list or CSV of event attendees: one person per line as `name, email, company` (email and company optional). */

export interface ParsedAttendee {
  name: string;
  email: string | null;
  company: string;
}
export interface ParsedAttendees {
  attendees: ParsedAttendee[];
  /** 1-based line numbers that could not be used (no name, or an email that is not an address). */
  rejected: number[];
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Splits one line on tab, semicolon or comma (the first of those the line contains), honouring "double quotes". */
export function splitLine(line: string): string[] {
  const delimiter = line.includes('\t') ? '\t' : line.includes(';') && !line.includes(',') ? ';' : ',';
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i] as string;
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

export function parseAttendees(text: string): ParsedAttendees {
  const attendees: ParsedAttendee[] = [];
  const rejected: number[] = [];
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  let first = true;
  lines.forEach((line, i) => {
    if (line.trim() === '') return;
    const cells = splitLine(line);
    const head = (cells[0] ?? '').toLowerCase();
    if (first && ['name', 'full name', 'fullname', 'visitor', 'attendee'].includes(head)) {
      first = false;
      return;
    }
    first = false;
    const name = (cells[0] ?? '').slice(0, 120);
    const email = (cells[1] ?? '').toLowerCase();
    if (name === '' || (email !== '' && !EMAIL.test(email))) {
      rejected.push(i + 1);
      return;
    }
    attendees.push({ name, email: email === '' ? null : email, company: (cells[2] ?? '').slice(0, 120) });
  });
  return { attendees, rejected };
}
